import { flattenColumnDefaultGroups } from "./columnDefaultRulesUtils";

export const MAPPING_SKIP_VALUE = "skip";

export const CATEGORY_PATH_EXCLUSIVE_TOKENS = new Set([
  "category_path",
  "google_product_category",
  "product_type",
]);

export const CATEGORY_LEVEL_TOKENS = new Set([
  "category",
  "categoria",
  "category_1",
  "category_2",
  "category_3",
  "category_4",
  "category_5",
  "category_6",
  "vendor_category",
  "section",
  "department",
]);

export const collectActiveMappingTokens = (mapping = {}, columnDefaultRules = {}) => {
  const mapped = new Set();
  Object.values(mapping).forEach((value) => {
    if (value && value !== MAPPING_SKIP_VALUE) {
      mapped.add(String(value));
    }
  });
  flattenColumnDefaultGroups(columnDefaultRules).forEach((rule) => {
    const target = String(rule?.target_field || rule?.target || "").trim();
    if (target) {
      mapped.add(target);
    }
  });
  return mapped;
};

export const isCategoryPathGroupActive = (mappedTokens) => (
  [...CATEGORY_PATH_EXCLUSIVE_TOKENS].some((token) => mappedTokens.has(token))
);

export const isCategoryLevelGroupActive = (mappedTokens) => (
  [...CATEGORY_LEVEL_TOKENS].some((token) => mappedTokens.has(token))
);

export const isCategoryTokenSuppressed = (token, mappedTokens) => {
  if (isCategoryPathGroupActive(mappedTokens) && CATEGORY_LEVEL_TOKENS.has(token)) {
    return true;
  }
  if (isCategoryLevelGroupActive(mappedTokens) && CATEGORY_PATH_EXCLUSIVE_TOKENS.has(token)) {
    return true;
  }
  return false;
};

export const resolveMappingTokenChipState = (token, mappedTokens) => {
  if (mappedTokens.has(token)) {
    return "active";
  }
  if (isCategoryTokenSuppressed(token, mappedTokens)) {
    return "suppressed";
  }
  return "inactive";
};

export const isMappingTokenOptionDisabled = (
  token,
  mappedTokens,
  { assignedColumn = null, currentColumn = null } = {},
) => {
  if (assignedColumn && String(assignedColumn) === String(currentColumn)) {
    return false;
  }
  return isCategoryTokenSuppressed(token, mappedTokens);
};

export const buildMappingTokenChipItems = (fieldKeys = [], mapping = {}, columnDefaultRules = {}) => {
  const legacyAssigned = Object.values(mapping || {}).filter(
    (field) => field && field !== MAPPING_SKIP_VALUE && !fieldKeys.includes(field),
  );
  const tokens = [...new Set([...fieldKeys, ...legacyAssigned])];
  const mappedTokens = collectActiveMappingTokens(mapping, columnDefaultRules);
  return tokens.map((token) => ({
    token,
    state: resolveMappingTokenChipState(token, mappedTokens),
  }));
};

export const buildMappingFieldSelectOptions = ({
  assignableFieldKeys = [],
  mapping = {},
  columnDefaultRules = {},
  currentColumn = null,
  skipValue = MAPPING_SKIP_VALUE,
}) => {
  const usedFields = Object.entries(mapping || {})
    .filter(([, value]) => value && value !== skipValue)
    .reduce((acc, [column, value]) => {
      acc[value] = column;
      return acc;
    }, {});

  const mappedTokens = collectActiveMappingTokens(mapping, columnDefaultRules);

  return [
    { value: skipValue, label: "skip" },
    ...assignableFieldKeys.map((field) => {
      const takenBy = usedFields[field];
      const disabledByAssignment = Boolean(takenBy && String(takenBy) !== String(currentColumn));
      const disabledByCategoryConflict = isMappingTokenOptionDisabled(field, mappedTokens, {
        assignedColumn: takenBy,
        currentColumn,
      });
      const disabled = disabledByAssignment || disabledByCategoryConflict;
      let label = field;
      if (disabledByAssignment) {
        label = `${field} (col. ${takenBy})`;
      } else if (disabledByCategoryConflict) {
        label = `${field}`;
      }
      return {
        value: field,
        label,
        disabled,
      };
    }),
  ];
};

export const buildMappingTargetFieldOptions = ({
  fieldKeys = [],
  mapping = {},
  columnDefaultRules = {},
  currentValue = "",
}) => {
  const mappedTokens = collectActiveMappingTokens(mapping, columnDefaultRules);
  return (fieldKeys || []).map((field) => {
    const disabled = isMappingTokenOptionDisabled(field, mappedTokens, {
      assignedColumn: currentValue === field ? currentValue : null,
      currentColumn: currentValue,
    });
    return {
      value: field,
      label: disabled && currentValue !== field ? `${field} (non compatibile)` : field,
      disabled: disabled && currentValue !== field,
    };
  });
};
